export interface PasswordStrength {
  score: number // 0-4
  label: string
  color: string
  suggestions: string[]
}

export const calculatePasswordStrength = (
  password: string
): PasswordStrength => {
  let score = 0
  const suggestions: string[] = []

  if (!password) {
    return {
      score: 0,
      label: 'password.strength.very_weak',
      color: 'text-red-500',
      suggestions: ['password.suggestions.enter_password']
    }
  }

  // Length check
  if (password.length >= 8) score++
  else suggestions.push('password.suggestions.min_length')

  if (password.length >= 12) score++

  // Lowercase letters
  if (/[a-z]/.test(password)) score++
  else suggestions.push('password.suggestions.lowercase')

  // Uppercase letters
  if (/[A-Z]/.test(password)) score++
  else suggestions.push('password.suggestions.uppercase')

  // Numbers
  if (/\d/.test(password)) score++
  else suggestions.push('password.suggestions.numbers')

  // Special characters
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++
  else suggestions.push('password.suggestions.special_chars')

  // Reduce score for common patterns
  if (
    /^[a-z]+$/.test(password) ||
    /^[A-Z]+$/.test(password) ||
    /^\d+$/.test(password)
  ) {
    score = Math.max(0, score - 1)
    suggestions.push('password.suggestions.one_type')
  }

  // Sequential characters
  if (
    /(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stuv|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)/i.test(
      password
    )
  ) {
    score = Math.max(0, score - 1)
    suggestions.push('password.suggestions.sequences')
  }

  // Repeated characters
  if (/(.)\1{2,}/.test(password)) {
    score = Math.max(0, score - 1)
    suggestions.push('password.suggestions.repeated')
  }

  // Normalize score to 0-4
  score = Math.min(4, Math.max(0, Math.floor(score / 1.5)))

  let label: string
  let color: string

  switch (score) {
    case 0:
      label = 'password.strength.very_weak'
      color = 'text-red-500'
      break
    case 1:
      label = 'password.strength.weak'
      color = 'text-orange-500'
      break
    case 2:
      label = 'password.strength.medium'
      color = 'text-yellow-500'
      break
    case 3:
      label = 'password.strength.strong'
      color = 'text-success'
      break
    case 4:
      label = 'password.strength.very_strong'
      color = 'text-success'
      break
    default:
      label = 'password.strength.very_weak'
      color = 'text-red-500'
  }

  return {
    score,
    label,
    color,
    suggestions: suggestions.slice(0, 2) // Limit to 2 suggestions
  }
}

export const validatePasswordMatch = (
  password: string,
  confirmPassword: string
): { isValid: boolean; message: string } => {
  if (!confirmPassword) {
    return { isValid: false, message: '' }
  }

  if (password !== confirmPassword) {
    return { isValid: false, message: 'password.match.mismatch' }
  }

  return { isValid: true, message: 'password.match.match' }
}
